import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/db';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { Card, Button, Badge, Input } from '@/components/ui/Components';
import SafeIcon from '@/common/SafeIcon';

const emptyForm = {
  name: '', location_limit: 1, expiry_date: '', admin_email: '', admin_password: '', notification_email: ''
};

const Businesses = () => {
  const [businesses, setBusinesses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    const data = await api.getAllBusinesses();
    setBusinesses(data);
  };

  const handleUpdate = async (id, field, value) => {
    try {
      await api.updateBusinessData(id, { [field]: value });
      addToast('Változások mentve!');
      fetchData();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.createBusiness(form);
      addToast(`"${form.name}" sikeresen létrehozva!`, 'success');
      setShowModal(false);
      setForm(emptyForm);
      fetchData();
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (biz) => {
    if (!confirm(`Biztosan törlöd a(z) "${biz.name}" vállalkozást? Ez az összes helyszínt és értékelést is törli!`)) return;
    try {
      await api.deleteBusiness(biz.id);
      addToast(`"${biz.name}" törölve`, 'success');
      fetchData();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleImpersonate = async (bizId, bizName) => {
    try {
      await impersonate(bizId);
      addToast(`Sikeres belépés: ${bizName}`, 'success');
      navigate('/admin');
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-gray-900">Ügyfelek kezelése</h1>
          <p className="text-gray-500">Vállalkozások, előfizetések és helyszín limitek kezelése.</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setShowModal(true); }}>
          <SafeIcon name="Plus" className="w-4 h-4 mr-2" /> Új vállalkozás
        </Button>
      </div>

      {/* ─── Létrehozás modal ─────────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg p-8 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">Új vállalkozás létrehozása</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <SafeIcon name="X" className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <Input
                label="Vállalkozás neve *"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Pl. Gasztró Kft."
                required
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Helyszín limit *"
                  type="number"
                  min="1"
                  value={form.location_limit}
                  onChange={e => setForm({ ...form, location_limit: parseInt(e.target.value) })}
                  required
                />
                <Input
                  label="Előfizetés vége *"
                  type="date"
                  value={form.expiry_date}
                  onChange={e => setForm({ ...form, expiry_date: e.target.value })}
                  required
                />
              </div>
              <div className="border-t pt-4">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Admin fiók adatai</p>
                <div className="space-y-3">
                  <Input
                    label="Admin e-mail *"
                    type="email"
                    value={form.admin_email}
                    onChange={e => setForm({ ...form, admin_email: e.target.value })}
                    placeholder="admin@vallalkozas.hu"
                    required
                  />
                  <Input
                    label="Admin jelszó *"
                    type="password"
                    value={form.admin_password}
                    onChange={e => setForm({ ...form, admin_password: e.target.value })}
                    placeholder="Min. 6 karakter"
                    required
                  />
                </div>
              </div>
              <Input
                label="Értesítési e-mail (negatív visszajelzéseknél)"
                type="email"
                value={form.notification_email}
                onChange={e => setForm({ ...form, notification_email: e.target.value })}
                placeholder="ertesites@vallalkozas.hu"
              />
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Mégse</Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Létrehozás...' : 'Vállalkozás létrehozása'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* ─── Táblázat ─────────────────────────────────────────────────── */}
      <Card className="overflow-hidden border-0 shadow-lg ring-1 ring-black/5">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Vállalkozás</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Státusz</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Helyszín limit</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Előfizetés vége</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Értesítési e-mail</th>
              <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Műveletek</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {businesses.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-400">
                  Még nincs vállalkozás. Kattints az "Új vállalkozás" gombra.
                </td>
              </tr>
            )}
            {businesses.map(biz => {
              const isActive = biz.expiry_date >= today;
              return (
                <tr key={biz.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingId === biz.id ? (
                      <input
                        autoFocus
                        className="border border-blue-400 rounded-lg px-2 py-1 text-sm font-bold focus:ring-2 focus:ring-blue-500 outline-none"
                        defaultValue={biz.name}
                        onBlur={(e) => { handleUpdate(biz.id, 'name', e.target.value); setEditingId(null); }}
                        onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                      />
                    ) : (
                      <div
                        className="font-bold text-gray-900 cursor-pointer hover:text-blue-600 flex items-center gap-1 group"
                        onClick={() => setEditingId(biz.id)}
                        title="Kattints a névhez a szerkesztéshez"
                      >
                        {biz.name}
                        <SafeIcon name="Pencil" className="w-3 h-3 opacity-0 group-hover:opacity-40" />
                      </div>
                    )}
                    <div className="text-xs text-gray-400 font-mono">ID: {biz.id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant={isActive ? 'green' : 'red'}>
                      {isActive ? 'Aktív' : 'Lejárt'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        defaultValue={biz.location_limit}
                        onBlur={(e) => handleUpdate(biz.id, 'location_limit', parseInt(e.target.value))}
                      />
                      <span className="text-xs text-gray-400">db</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="date"
                      className="border border-gray-200 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50"
                      defaultValue={biz.expiry_date}
                      onBlur={(e) => handleUpdate(biz.id, 'expiry_date', e.target.value)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="email"
                      className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50 w-44"
                      placeholder="(nincs beállítva)"
                      defaultValue={biz.notification_email || ''}
                      onBlur={(e) => handleUpdate(biz.id, 'notification_email', e.target.value)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleImpersonate(biz.id, biz.name)}>
                        <SafeIcon name="LogIn" className="w-4 h-4 mr-1" /> Belépés
                      </Button>
                      <button
                        onClick={() => handleDelete(biz)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Vállalkozás törlése"
                      >
                        <SafeIcon name="Trash2" className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default Businesses;
